// Sentry initialiser — extracted from `src/main.tsx` as a test seam.
//
// `src/main.tsx` cannot be imported from Vitest under Node because its module
// body calls `createRoot(document.getElementById("root")!)` which requires a
// DOM. Keeping the init logic in this side-effect-free module lets the unit
// suite at `src/tests/sentryClient.test.ts` exercise the DSN guard, sampling
// rates, integrations, and release plumbing without booting React.

import * as Sentry from '@sentry/react';

/**
 * Minimal Sentry namespace shape used by `syncSentryUser`. Defined as an
 * interface so the helper can be unit-tested with a mocked object instead of
 * pulling in the real `@sentry/react` package.
 */
export interface SentryUserSink {
  setUser: (user: { id: string; email?: string } | null) => void;
}

/**
 * Forward only the bare-minimum identifying info (`id`, `email`) to Sentry on
 * auth state changes. We intentionally strip every other field on the
 * Supabase user object — name, phone, custom metadata — to limit PII exposure
 * in error reports. Test seam for `src/tests/sentryClient.test.ts`.
 *
 * Lives here (not in `useAuth.tsx`) so the unit suite doesn't have to pull
 * in the Redux store, the Supabase client, or any other AuthProvider deps.
 */
export function syncSentryUser(
  sentry: SentryUserSink,
  session: { user?: { id: string; email?: string | null } | null } | null
): void {
  const user = session?.user;
  if (user) {
    sentry.setUser({
      id: user.id,
      email: user.email ?? undefined,
    });
  } else {
    sentry.setUser(null);
  }
}

/**
 * Initialise Sentry for the React SPA.
 *
 * Behaviour:
 *   - Empty / missing / whitespace `dsn` → skip `Sentry.init`, log a single
 *     `console.warn`. This is the typical local-dev path where no DSN is
 *     configured.
 *   - Present `dsn` → call `Sentry.init` once with the documented sampling
 *     rates (0.2 traces, 1.0 replays-on-error, 0.01 replays-session) and the
 *     two integrations (browserTracing + replay).
 *   - `release` is only forwarded when truthy so dev builds without a release
 *     SHA don't show up as `release: undefined` in Sentry.
 */
export function initSentry(env: {
  dsn?: string;
  mode?: string;
  release?: string;
}): void {
  const dsn = env.dsn;
  if (!dsn || dsn.trim().length === 0) {
    // Single, friendly warning so devs without a DSN aren't confused but also
    // aren't spammed every render.
    console.warn(
      'Sentry: VITE_SENTRY_DSN not set — error tracking disabled for this session.'
    );
    return;
  }
  const initPayload: Parameters<typeof Sentry.init>[0] = {
    dsn,
    environment: env.mode,
    tracesSampleRate: 0.2,
    replaysOnErrorSampleRate: 1.0,
    replaysSessionSampleRate: 0.01,
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration(),
    ],
  };
  if (env.release) {
    initPayload.release = env.release;
  }
  Sentry.init(initPayload);
}
